import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { LotBuildProgrammeEventService } from '../../../services/lot-build-programme-event.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { LotBuildProgrammeActionBarService } from '../../services/lot-build-programme-action-bar.service';
import { forIn, orderBy } from 'lodash';
import { UserCacheService } from '@app/core/services/user-cache/user-cache.service';
import { UserCacheItem } from '@app/core/services/user-cache/user-cache-item';
import { ILotBuildProgrammeView } from '@app/core/services/user-cache/user-cache-areas';
import {
    IBuildProgrammeStageIndexedDto,
    ILotDto,
    SSR_STATE_ENUM
} from '@classictechsolutions/hubapi-transpiled-enums';
import { CompleteBuildProgrammeActivitiesDialogComponent } from '../complete-build-programme-activities-dialog/complete-build-programme-activities-dialog.component';
import { CbDialogService } from '@app/shared/components/dialog/cb-dialog.service';
import { LotBuildPermissions } from '@app/core/permissions';
import { NavigationService } from '@app/core/services/navigation/navigation.service';

import { getCostActionDisabledText, isCostActionDisabled } from '@app/shared/utils/lot.util';

@Component({
    selector: 'cb-lot-build-programme-stage-list',
    templateUrl: './lot-build-programme-stage-list.component.html',
    styleUrls: ['./lot-build-programme-stage-list.component.scss'],
    standalone: false
})
export class LotBuildProgrammeStageListComponent implements OnInit, OnDestroy {
    @Input() public lotDto: ILotDto;

    public loaded$$: BehaviorSubject<boolean> = new BehaviorSubject(null);
    public expandedPanels: { [buildStageId: number]: boolean } = {};
    public get cache(): UserCacheItem<ILotBuildProgrammeView> {
        return this.userCacheService?.lotBuildProgrammeView;
    }

    public visibleStages:  IBuildProgrammeStageIndexedDto[] = [];

    public stageFlags: {
        [stageId: number]: {
            canCompleteSsrs: boolean;
            canConfirmSsrs: boolean;
            canGenerateSsrs: boolean;
        };
    } = {};

    private readonly $subs = new Subscription();

    public queryParamsLoaded = false;
    public highlightedActivityId: number | null;

    constructor(
        public readonly lotBuildProgrammePermissions: LotBuildPermissions,
        public readonly lotBuildProgrammeEvents: LotBuildProgrammeEventService,
        private readonly lotBuildProgrammeActionBar: LotBuildProgrammeActionBarService,
        private readonly userCacheService: UserCacheService,
        public readonly cbDialog: CbDialogService,
        public readonly navigationService: NavigationService
    ) {}

    public ngOnInit(): void {
        this.setSubs();
        this.userCacheService.lotBuildProgrammeView.init().then(() => {
            this.expandedPanels = this.cache.copyData();
            this.lotBuildProgrammeEvents.buildStages?.$promise?.then(() => {
                this.filterStages();
            });
        });
    }

    public ngOnDestroy(): void {
        this.$subs.unsubscribe();
    }

    private setSubs(): void {
        this.$subs.add(
            this.lotBuildProgrammeActionBar.VIEW_ALL_STAGES_CHANGED.subscribe(this.filterStages)
        );
        this.$subs.add(this.lotBuildProgrammeEvents.STAGE_RECEIVED.subscribe(this.filterStages));
        this.$subs.add(this.lotBuildProgrammeEvents.ACTIVITY_RECEIVED.subscribe(this.filterStages));
        this.$subs.add(
            this.lotBuildProgrammeActionBar.EXPAND_COLLAPSE_ALL.subscribe(this.expandCollapseAll)
        );
        this.$subs.add(
            this.lotBuildProgrammeEvents.STAGE_REQUIRES_UPDATE.subscribe(this.updateStageFlags)
        );
    }

    public anyActivitiesAvailable(): boolean {
        return (
            Object.values(this.lotBuildProgrammeEvents.activities).filter(a => a.length > 0)
                .length > 0
        );
    }

    private readonly updateStageFlags = (buildStageId: number): void => {
        const activities = this.lotBuildProgrammeEvents.activities[buildStageId];
        this.stageFlags[buildStageId] = {
            canCompleteSsrs: activities?.some(x => x?.rules?.canCompleteSsr),
            canConfirmSsrs: activities?.some(x => x?.rules?.canConfirmSsr),
            canGenerateSsrs: activities?.some(x => x?.rules?.canGenerateSsr)
        };
    };

    public generateSsrs(event: MouseEvent, buildStageId: number): void {
        event.stopPropagation();
        this.cbDialog.open(CompleteBuildProgrammeActivitiesDialogComponent, {
            data: {
                targetSsrState: SSR_STATE_ENUM.Draft,
                buildStageId
            }
        });
    }

    public confirmSsrs(event: MouseEvent, buildStageId: number): void {
        event.stopPropagation();
        this.cbDialog.open(CompleteBuildProgrammeActivitiesDialogComponent, {
            data: {
                targetSsrState: SSR_STATE_ENUM.Confirmed,
                buildStageId
            }
        });
    }

    public completeActivities(event: MouseEvent, buildStageId: number): void {
        event.stopPropagation();
        this.cbDialog.open(CompleteBuildProgrammeActivitiesDialogComponent, {
            data: {
                targetSsrState: SSR_STATE_ENUM.Completed,
                buildStageId
            }
        });
    }

    public togglePinnedPanel(event: MouseEvent, buildStageId: number): void {
        event.stopPropagation();
        this.cache.data[buildStageId] = !this.cache.data[buildStageId];
    }

    public readonly expandCollapseAll = (): void => {
        this.resetAllExpanded();
        const result = !this.lotBuildProgrammeActionBar.allExpanded;
        this.visibleStages.forEach(stage => {
            this.expandedPanels[stage.buildStageId] = result;
        });
        this.lotBuildProgrammeActionBar.allExpanded = result;
        this.lotBuildProgrammeActionBar.POST_EXPAND_COLLAPSE_ALL.next(result);
    };

    public getAllExpanded(): boolean {
        const expandedPanelsCount = this.getExpandedPanelCount();
        const panelCount = this.visibleStages?.length ?? 0;
        return expandedPanelsCount >= panelCount;
    }

    public getExpandedPanelCount(): number {
        let expandedPanelsCount = 0;
        forIn(this.expandedPanels, (value, key) => {
            if (value === true) {
                expandedPanelsCount++;
            }
        });
        return expandedPanelsCount;
    }

    public resetAllExpanded(): void {
        this.lotBuildProgrammeActionBar.allExpanded = this.getAllExpanded();
    }

    public readonly filterStages = (): void => {
        const stages = this.lotBuildProgrammeActionBar.showAll
            ? this.getAllStages()
            : this.lotBuildProgrammeEvents.stages?.filter(
                stage => this.lotBuildProgrammeEvents.activities[stage.buildStageId]?.length > 0
            );

        this.visibleStages = orderBy(
            stages,
            'buildStageSortOrder',
            'asc'
        ) as IBuildProgrammeStageIndexedDto[];
        this.visibleStages.forEach(stage => this.updateStageFlags(stage.buildStageId));
        this.resetAllExpanded();
        this.loaded$$.next(true);
        this.loadQueryParams();
    };

    private loadQueryParams(): void {
        if (this.queryParamsLoaded || this.visibleStages?.length < 1) {
            return;
        }
        this.queryParamsLoaded = true;
        const params = this.navigationService.getSnapshotQueryParams<{
            buildProgrammeActivityId?: number;
            paramEntityId?: number;
        }>();

        this.highlightedActivityId =
            Number(params.buildProgrammeActivityId ?? params.paramEntityId) || null;
        if (this.highlightedActivityId > 0) {
            const stage = this.visibleStages.find(candidateStage =>
                candidateStage.buildProgrammeActivities?.some(activityId => activityId === this.highlightedActivityId)
            );
            if (stage) {
                this.expandedPanels[stage.buildStageId] = true;
            }
        }
    }

    public getAllStages(): IBuildProgrammeStageIndexedDto[] {
        this.lotBuildProgrammeEvents?.buildStages.forEach(buildStage => {
            const exists = this.lotBuildProgrammeEvents.buildStageIndex[buildStage.id] != null;
            if (!exists) {
                const newBuildProgrammeStage: IBuildProgrammeStageIndexedDto = {
                    buildProgrammeActivities: [],
                    buildStageCode: buildStage.code,
                    buildStageId: buildStage.id,
                    buildStageLabel: buildStage.label,
                    buildStageSortOrder: buildStage.sortOrder,
                    id: 0 - buildStage.id,
                    sortOrder: 0,
                    buildProgrammeId: this.lotBuildProgrammeEvents?.buildProgramme?.id
                };
                this.lotBuildProgrammeEvents.STAGE_RECEIVED.next([newBuildProgrammeStage]);
            }
        });

        return this.lotBuildProgrammeEvents.stages;
    }

    protected readonly isCostActionDisabled = isCostActionDisabled;
    protected readonly getCostActionDisabledText = getCostActionDisabledText;
}
